import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatDate, euro, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDelete from "@/components/ConfirmDelete";
import SottocommesseTable from "@/components/SottocommesseTable";
import MultiSelect from "@/components/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, FolderOpen, FileUp, FileText, Table2, Loader2, ShieldCheck, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

const emptySub = { codice: "", nome: "", comune: "", indirizzo: "", id_cliente: "", tipo: "", listini_ids: [] };

export default function CommessaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [all, setAll] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySub);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tipiDoc, setTipiDoc] = useState([]);
  const [docTipo, setDocTipo] = useState("");
  const [docFilter, setDocFilter] = useState("__all__");
  const [campi, setCampi] = useState([]);
  const [tipiCommessa, setTipiCommessa] = useState([]);
  const [svincoli, setSvincoli] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [fattureCount, setFattureCount] = useState({});
  const [listini, setListini] = useState([]);
  const fileRef = useRef(null);

  const load = () => api.get("/commesse").then((r) => setAll(r.data));
  useEffect(() => {
    load();
    api.get("/tipi_documento").then((r) => setTipiDoc(r.data));
    api.get("/campi_sottocommessa").then((r) => setCampi(r.data));
    api.get("/tipi_commessa").then((r) => setTipiCommessa(r.data));
    api.get("/listini").then((r) => setListini(r.data)).catch(() => {});
    api.get("/fatture/count-by-commessa").then((r) => setFattureCount(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (id) {
      api.get(`/commesse/${id}/svincoli`).then((r) => setSvincoli(r.data)).catch(() => setSvincoli([]));
      api.get(`/commesse/${id}/fatture`).then((r) => setFatture(r.data)).catch(() => setFatture([]));
    }
  }, [id]);

  const commessa = all.find((c) => c.id === id);
  const isSub = !!commessa?.parent_id;
  const parent = isSub ? all.find((c) => c.id === commessa.parent_id) : null;
  const children = all.filter((c) => c.parent_id === id);
  const hasChildren = children.length > 0;
  const openFattura = (f) => navigate(`/fatture?fattura=${f.id}`);

  // ---- Sottocommesse (vista madre) ----
  const openNew = () => { setForm({ ...emptySub, listini_ids: commessa?.listini_ids || [] }); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ codice: s.codice, nome: s.nome, comune: s.comune || "", indirizzo: s.indirizzo || "", id_cliente: s.id_cliente || "", tipo: s.tipo || "", listini_ids: s.listini_ids || [] }); setEditing(s.id); setOpen(true); };
  const saveSub = async () => {
    if (!form.codice.trim() || !form.nome.trim()) return toast.error("Codice ODA e nome sono obbligatori");
    if (!form.comune.trim() || !form.indirizzo.trim()) return toast.error("Comune e indirizzo sono obbligatori");
    try {
      if (editing) await api.put(`/commesse/${editing}`, { ...form });
      else await api.post("/commesse", { ...form, parent_id: id });
      toast.success(editing ? "Sottocommessa aggiornata" : "Sottocommessa creata");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeSub = async (sid) => { await api.delete(`/commesse/${sid}`); toast.success("Sottocommessa eliminata"); load(); };

  // ---- Dettagli (vista sottocommessa) ----
  const [det, setDet] = useState({ ...emptySub, dettagli_extra: {} });
  const [editDet, setEditDet] = useState(false);
  const initDet = (c) => ({ codice: c.codice, nome: c.nome, via: c.via || "", comune: c.comune || "", indirizzo: c.indirizzo || "", id_cliente: c.id_cliente || "", tipo: c.tipo || "", bef_mode: c.bef_mode || "fatture", descrizione: c.descrizione || "", problema_noto: c.problema_noto || "", dettagli_extra: { ...(c.dettagli_extra || {}) } });
  useEffect(() => {
    if (commessa) setDet(initDet(commessa));
  }, [commessa?.id]); // eslint-disable-line
  const setCampo = (cid, v) => setDet((d) => ({ ...d, dettagli_extra: { ...d.dettagli_extra, [cid]: v } }));
  const saveDettagli = async () => {
    try { await api.put(`/commesse/${id}`, { ...det }); toast.success("Dettagli salvati"); setEditDet(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancelDettagli = () => {
    if (commessa) setDet(initDet(commessa));
    setEditDet(false);
  };

  // ---- Documenti ----
  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try { const fd = new FormData(); fd.append("file", f); if (docTipo) fd.append("tipo", docTipo); await api.post(`/commesse/${id}/documenti`, fd); }
      catch (err) { toast.error(`${f.name}: caricamento non riuscito`); }
    }
    setUploading(false); toast.success("Documenti caricati"); load();
  };
  const viewDoc = async (d) => {
    try { const res = await api.get(`/commesse/${id}/documenti/${d.id}/file`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(new Blob([res.data], { type: d.content_type || "application/octet-stream" })), "_blank");
    } catch (e) { toast.error("Documento non disponibile"); }
  };
  const removeDoc = async (docId) => { await api.delete(`/commesse/${id}/documenti/${docId}`); toast.success("Documento eliminato"); load(); };

  const viewSvincolo = async (s) => {
    try {
      const res = await api.get(`/fatture/${s.id}/file`, { responseType: "blob" });
      window.open(window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error("PDF non disponibile"); }
  };
  const totSvincolato = svincoli.reduce((s, x) => s + (x.importo || 0), 0);

  if (!commessa) return <div className="p-8 text-slate-400">Caricamento…</div>;

  return (
    <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-4">
      <Button variant="ghost" size="sm" data-testid="detail-back" onClick={() => navigate(isSub ? `/commesse/${parent?.id}` : "/commesse")} className="text-slate-600 hover:text-blue-700 -ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> {isSub ? `Torna a ${parent?.codice || "commessa"}` : "Tutte le commesse"}
      </Button>
      <PageHeader
        title={`${commessa.codice} · ${commessa.nome}`}
        subtitle={isSub ? `Sottocommessa di ${parent?.codice} · Cliente: ${commessa.cliente_nome || "—"}` : `Commessa · Cliente: ${commessa.cliente_nome || "—"}`}
      >
        {!hasChildren && (
          <Button data-testid="sub-open-contabilita" onClick={() => navigate(`/contabilita?commessa=${id}`)} className="bg-blue-700 hover:bg-blue-800">
            <Table2 className="h-4 w-4 mr-1.5" /> Apri Contabilità
          </Button>
        )}
      </PageHeader>

      {hasChildren ? (
        // ===== Vista COMMESSA MADRE: gestione sottocommesse =====
        <Card className="rounded-md border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Sottocommesse</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" data-testid="export-contabilita-completa" onClick={() => downloadFile(`/commesse/${id}/export/excel-completo`, `contabilita_completa_${commessa.codice}.xlsx`).catch(() => toast.error("Errore durante l'esportazione"))}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-700" /> Esporta contabilità completa
              </Button>
              <Button data-testid="add-sottocommessa" onClick={openNew} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-1.5" /> Aggiungi sottocommessa</Button>
            </div>
          </div>
          {children.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Nessuna sottocommessa. Se la commessa ha sottocommesse, la contabilità va gestita al loro interno.</p>
          ) : (
            <SottocommesseTable
              subs={children}
              mode="commesse"
              fattureCount={fattureCount}
              onOpen={(s) => navigate(`/commesse/${s.id}`)}
              onEdit={openEdit}
              onDelete={removeSub}
              isAdmin={isAdmin}
              currentUserId={user?.id}
            />
          )}
        </Card>
      ) : (
        // ===== Vista SOTTOCOMMESSA: contenitore con schede =====
        <Tabs defaultValue="dettagli">
          <TabsList data-testid="sub-tabs">
            <TabsTrigger value="dettagli" data-testid="tab-dettagli">Dettagli</TabsTrigger>
            <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti {commessa.documenti?.length ? `(${commessa.documenti.length})` : ""}</TabsTrigger>
            <TabsTrigger value="fatture" data-testid="tab-fatture">Fatture associate {fatture.length ? `(${fatture.length})` : ""}</TabsTrigger>
            <TabsTrigger value="svincoli" data-testid="tab-svincoli">Storico svincoli {svincoli.length ? `(${svincoli.length})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="dettagli">
            <Card className="rounded-md border-slate-200 shadow-sm p-5 max-w-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-slate-900">Dettagli {isSub ? "sottocommessa" : "commessa"}</h3>
                {isAdmin && !editDet && (
                  <Button variant="outline" size="sm" data-testid="edit-dettagli" onClick={() => setEditDet(true)}>
                    <Pencil className="h-4 w-4 mr-1.5" /> Modifica
                  </Button>
                )}
              </div>
              {editDet ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm text-slate-700">Codice ODA</Label><Input data-testid="det-codice" value={det.codice} onChange={(e) => setDet({ ...det, codice: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-sm text-slate-700">Nome</Label><Input data-testid="det-nome" value={det.nome} onChange={(e) => setDet({ ...det, nome: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm text-slate-700">Comune</Label><Input data-testid="det-comune" value={det.comune} onChange={(e) => setDet({ ...det, comune: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-sm text-slate-700">Indirizzo</Label><Input data-testid="det-indirizzo" value={det.indirizzo} onChange={(e) => setDet({ ...det, indirizzo: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm text-slate-700">ID Cliente</Label><Input data-testid="det-idcliente" value={det.id_cliente} onChange={(e) => setDet({ ...det, id_cliente: e.target.value })} className="mt-1.5" /></div>
                    <div>
                      <Label className="text-sm text-slate-700">Tipo</Label>
                      <Select value={det.tipo || "__none__"} onValueChange={(v) => setDet({ ...det, tipo: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="det-tipo-select" className="mt-1.5 bg-white"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nessuno —</SelectItem>
                          {tipiCommessa.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Crea i valori in Anagrafiche → Tipo</div>}
                          {tipiCommessa.map((t) => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">Riconoscimento BEF</Label>
                    <Select value={det.bef_mode || "fatture"} onValueChange={(v) => setDet({ ...det, bef_mode: v })}>
                      <SelectTrigger data-testid="det-befmode-select" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fatture">Da fatture associate</SelectItem>
                        <SelectItem value="pagato">Manuale: "Pagato" per riga</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1.5">In modalità "Pagato per riga" il Riconosciuto (BEF) somma solo le righe di contabilità marcate come pagate; le fatture servono solo a catalogare il fatturato.</p>
                  </div>
                  {campi.length === 0 && (
                    <p className="text-sm text-slate-400">Nessun campo aggiuntivo. Definiscine in <span className="font-medium text-slate-500">Anagrafiche → Campi sottocommessa</span>.</p>
                  )}
                  {campi.map((f) => {
                    const val = det.dettagli_extra?.[f.id] || "";
                    return (
                      <div key={f.id}>
                        <Label className="text-sm text-slate-700">{f.nome}</Label>
                        {f.tipo === "testo_lungo" ? (
                          <Textarea data-testid={`det-campo-${f.id}`} value={val} onChange={(e) => setCampo(f.id, e.target.value)} className="mt-1.5" rows={3} />
                        ) : (
                          <Input data-testid={`det-campo-${f.id}`} type={f.tipo === "numero" ? "number" : f.tipo === "data" ? "date" : "text"} value={val} onChange={(e) => setCampo(f.id, e.target.value)} className="mt-1.5" />
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" data-testid="cancel-dettagli" onClick={cancelDettagli}>Annulla</Button>
                    <Button data-testid="save-dettagli" onClick={saveDettagli} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
                  </div>
                </div>
              ) : (
                <dl className="divide-y divide-slate-100">
                  {[["Codice ODA", det.codice], ["ID Cliente", det.id_cliente], ["Tipo", det.tipo], ["Nome", det.nome], ["Comune", det.comune], ["Indirizzo", det.indirizzo], ["Riconoscimento BEF", det.bef_mode === "pagato" ? 'Pagato per riga' : 'Da fatture associate'], ...campi.map((f) => [f.nome, det.dettagli_extra?.[f.id]])].map(([k, v], i) => (
                    <div key={i} className="flex gap-4 py-2.5">
                      <dt className="w-40 text-sm font-medium text-slate-500 flex-shrink-0">{k}</dt>
                      <dd data-testid={`det-view-${k}`} className="text-sm text-slate-800 whitespace-pre-wrap">{v || <span className="text-slate-300">—</span>}</dd>
                    </div>
                  ))}
                  {campi.length === 0 && (
                    <p className="text-xs text-slate-400 pt-3">Aggiungi campi in Anagrafiche → Campi sottocommessa per arricchire questa scheda.</p>
                  )}
                </dl>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="documenti">
            <Card className="rounded-md border-slate-200 shadow-sm p-5 max-w-2xl">
              <input ref={fileRef} type="file" multiple onChange={uploadDoc} className="hidden" data-testid="doc-input" />
              {isAdmin && (
                <div className="flex items-center justify-end gap-2 mb-4">
                  <Select value={docTipo} onValueChange={setDocTipo}>
                    <SelectTrigger data-testid="doc-tipo-select" className="w-52 bg-white"><SelectValue placeholder="Tipo documento" /></SelectTrigger>
                    <SelectContent>
                      {tipiDoc.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Crea i tipi in Anagrafiche</div>}
                      {tipiDoc.map((t) => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="upload-doc" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline">
                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileUp className="h-4 w-4 mr-1.5" />} Carica documenti
                  </Button>
                </div>
              )}
              {(commessa.documenti || []).length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Filtra per tipo</span>
                  <Select value={docFilter} onValueChange={setDocFilter}>
                    <SelectTrigger data-testid="doc-filter-select" className="w-52 h-9 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Tutti i tipi</SelectItem>
                      {[...new Set(commessa.documenti.map((d) => d.tipo).filter(Boolean))].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      {commessa.documenti.some((d) => !d.tipo) && <SelectItem value="__none__">Senza tipo</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(() => {
                const docs = (commessa.documenti || []).filter((d) =>
                  docFilter === "__all__" || (docFilter === "__none__" ? !d.tipo : d.tipo === docFilter));
                return docs.length === 0 ? (
                  <p className="text-sm text-slate-400 py-8 text-center" data-testid="doc-empty">
                    {(commessa.documenti || []).length === 0 ? "Nessun documento caricato." : "Nessun documento per il tipo selezionato."}
                  </p>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                    {docs.map((d) => (
                      <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                        <FileText className="h-4 w-4 text-rose-500 flex-shrink-0" />
                        <button onClick={() => viewDoc(d)} data-testid={`doc-open-${d.id}`} className="flex-1 text-left text-blue-700 hover:underline truncate">{d.name}</button>
                        {d.tipo && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-normal" data-testid={`doc-tipo-${d.id}`}>{d.tipo}</Badge>}
                        <span className="text-xs text-slate-400">{formatDate(d.uploaded_at)}</span>
                        {isAdmin && (
                          <ConfirmDelete title="Eliminare documento?" description={`"${d.name}" verrà rimosso.`} actionTestid={`confirm-del-doc-${d.id}`} onConfirm={() => removeDoc(d.id)}>
                            <Button variant="ghost" size="icon" data-testid={`del-doc-${d.id}`} className="h-8 w-8 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                          </ConfirmDelete>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Card>
          </TabsContent>

          <TabsContent value="fatture">
            <Card className="rounded-md border-slate-200 shadow-sm p-5 max-w-2xl" data-testid="fatture-associate-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-slate-900">Fatture associate</h3>
                {fatture.length > 0 && (
                  <span className="text-sm text-slate-500">Totale imponibile <strong className="text-blue-800 tabular-nums">{euro(fatture.reduce((s, f) => s + (f.importo || 0), 0))}</strong></span>
                )}
              </div>
              {fatture.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center" data-testid="fatture-associate-empty">Nessuna fattura associata a questa commessa.</p>
              ) : (
                <div className="border border-slate-100 rounded-md divide-y divide-slate-100">
                  {fatture.map((f) => (
                    <button key={f.id} data-testid={`fattura-assoc-${f.id}`} onClick={() => openFattura(f)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 text-left">
                      <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{f.is_compensazione && <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 py-0.5" data-testid={`commessa-fattura-comp-${f.id}`}>Compensazione BEF</span>}{f.numero_fattura || "—"}{f.committente ? ` · ${f.committente}` : ""}</div>
                        <div className="text-xs text-slate-400">{formatDate(f.data_fattura || f.data_emissione)}{f.ritenuta ? ` · Ritenuta ${euro(f.ritenuta)}` : ""}</div>
                      </div>
                      <span className="text-sm font-semibold text-blue-800 tabular-nums flex-shrink-0" data-testid={`fattura-assoc-importo-${f.id}`}>{euro(f.importo)}</span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="svincoli">
            <Card className="rounded-md border-slate-200 shadow-sm p-5 max-w-2xl" data-testid="storico-svincoli">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-amber-600" />
                  <h3 className="font-heading font-semibold text-slate-900">Storico svincoli ritenute</h3>
                </div>
                {svincoli.length > 0 && (
                  <span className="text-sm text-slate-500">Totale recuperato <strong className="text-emerald-700 tabular-nums">{euro(totSvincolato)}</strong></span>
                )}
              </div>
              {svincoli.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">Nessuna fattura di svincolo registrata per questa commessa.</p>
              ) : (
                <div className="border border-slate-100 rounded-md divide-y divide-slate-100">
                  {svincoli.map((s) => (
                    <div key={s.id} data-testid={`svincolo-row-${s.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                      <ShieldCheck className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{s.numero_fattura || "—"}{s.committente ? ` · ${s.committente}` : ""}</div>
                        <div className="text-xs text-slate-400">{formatDate(s.data_fattura || s.data_emissione)}{s.note ? ` · ${s.note}` : ""}</div>
                      </div>
                      <span className="text-sm font-semibold text-emerald-700 tabular-nums flex-shrink-0" data-testid={`svincolo-importo-${s.id}`}>{euro(s.importo)}</span>
                      {s.has_pdf && (
                        <Button variant="ghost" size="icon" data-testid={`svincolo-pdf-${s.id}`} title="Apri PDF" onClick={() => viewSvincolo(s)} className="h-8 w-8 flex-shrink-0 text-slate-400 hover:text-rose-600"><FileText className="h-4 w-4" /></Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica sottocommessa" : "Nuova sottocommessa"}</DialogTitle>
            <DialogDescription>Il cliente è ereditato dalla commessa madre.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm text-slate-700">Codice ODA *</Label><Input data-testid="sub-codice-input" value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} className="mt-1.5" placeholder="ODA-…" /></div>
              <div><Label className="text-sm text-slate-700">Nome *</Label><Input data-testid="sub-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm text-slate-700">Comune *</Label><Input data-testid="sub-comune-input" value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} className="mt-1.5" placeholder="Es. Napoli" /></div>
              <div><Label className="text-sm text-slate-700">Indirizzo *</Label><Input data-testid="sub-indirizzo-input" value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="mt-1.5" placeholder="Es. Via Roma 12" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm text-slate-700">ID Cliente</Label><Input data-testid="sub-idcliente-input" value={form.id_cliente} onChange={(e) => setForm({ ...form, id_cliente: e.target.value })} className="mt-1.5" placeholder="Facoltativo" /></div>
              <div>
                <Label className="text-sm text-slate-700">Tipo</Label>
                <Select value={form.tipo || "__none__"} onValueChange={(v) => setForm({ ...form, tipo: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="sub-tipo-select" className="mt-1.5 bg-white"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {tipiCommessa.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Crea i valori in Anagrafiche → Tipo</div>}
                    {tipiCommessa.map((t) => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-sm text-slate-700">Listino {editing ? "" : "(ereditato dalla madre)"}</Label>
                <div className="mt-1.5">
                  <MultiSelect testid="sub-listino-select" placeholder="Nessun listino"
                    value={form.listini_ids || []} onChange={(v) => setForm({ ...form, listini_ids: v })}
                    options={listini.map((l) => ({ value: l.id, label: `${l.nome}${l.tipo === "subappalto" ? " · subappalto" : ""}` }))} />
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400">Gli altri dettagli (definiti in Anagrafiche → Campi sottocommessa) si compilano dalla scheda della sottocommessa.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-sub-button" onClick={saveSub} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
